import {
  sanitizeWorkExperienceDuties,
  generateDutyTitleFromDescription,
} from './constants';

const BASELINE_KEY = '_baseline';

// ── Field mapping helpers ────────────────────────────────────────────────────

/** Month/year → API date string `MM/YYYY`, else null. */
function toApiDate(month, year) {
  if (!month || !year) return null;
  return `${String(month).padStart(2, '0')}/${year}`;
}

/**
 * Form `employment_period` → API `{ from_date, to_date, is_current }`.
 * Dates are sent as `MM/YYYY` (the backend's format); the picker only captures
 * month + year.
 */
export function periodToApi(period = {}) {
  const p = period || {};
  const isCurrent = !!p.present;
  return {
    from_date: toApiDate(p.startMonth, p.startYear),
    to_date: isCurrent ? null : toApiDate(p.endMonth, p.endYear),
    is_current: isCurrent,
  };
}

/** New work experience → full create payload (duties embedded, no id). */
export function buildCreateExperience(exp) {
  const duties = sanitizeWorkExperienceDuties(exp).duties.map(
    ({ title, description }) => ({ title, description })
  );
  return {
    company_name: exp.company || '',
    role: exp.role || '',
    ...periodToApi(exp.employment_period),
    duties,
  };
}

/**
 * Existing work experience → `{ id, company_name, role, from_date, to_date,
 * is_current }` for the edit payload, or `null` when nothing at the
 * experience level changed. The snapshot comparison still decides *whether*
 * to send an update at all (untouched rows stay silent), but once a row is
 * touched, every field goes — not just the ones that changed — since the
 * backend expects the full row on update. Duties travel through their own
 * CRUD and are never part of this payload.
 */
export function buildExistingExperienceUpdate(exp, snapshot) {
  const cur = {
    company_name: exp.company || '',
    role: exp.role || '',
    ...periodToApi(exp.employment_period),
  };
  const old = {
    company_name: snapshot.company || '',
    role: snapshot.role || '',
    ...periodToApi(snapshot.employment_period),
  };
  const hasChange = [
    'company_name',
    'role',
    'from_date',
    'to_date',
    'is_current',
  ].some((k) => cur[k] !== old[k]);
  if (!hasChange) return null;
  return { id: exp.id, ...cur };
}

/**
 * Diff current form experiences against the last-loaded server snapshot.
 * Returns `{ creates, updates, deleteIds }`:
 *  - creates: new rows (no id) as full create payloads with embedded duties
 *  - updates: existing rows that changed, as the full row `{ id, company_name,
 *    role, from_date, to_date, is_current }` (not just the changed fields —
 *    the snapshot comparison only decides whether a row is included at all)
 *  - deleteIds: ids present in the snapshot but gone from the form
 */
export function diffWorkExperiences(snapshot, current) {
  const snapById = new Map(
    (snapshot || []).filter((e) => e && e.id != null).map((e) => [e.id, e])
  );

  const creates = [];
  const updates = [];

  (current || []).forEach((exp) => {
    if (!exp) return;
    if (exp.id == null) {
      creates.push(buildCreateExperience(exp));
      return;
    }
    const snap = snapById.get(exp.id);
    const upd = snap ? buildExistingExperienceUpdate(exp, snap) : null;
    if (upd) updates.push(upd);
  });

  const currentIds = new Set(
    (current || []).filter((e) => e && e.id != null).map((e) => e.id)
  );
  const deleteIds = (snapshot || [])
    .filter((e) => e && e.id != null && !currentIds.has(e.id))
    .map((e) => e.id);

  return { creates, updates, deleteIds };
}

/**
 * Collect live duty CRUD ops that still need flushing at Save-Changes time.
 * Only existing experiences are considered — duties of new experiences are
 * embedded in their create payload. A duty needs flushing when it has no id
 * (added but never live-saved) or its value differs from its baseline (edited
 * but never live-saved). Empty-description rows are skipped.
 *
 * Returns ops: `{ type: 'create'|'update', expId, dutyId?, body:{title,description} }`.
 */
export function collectDutyFlushOps(current) {
  const ops = [];
  (current || []).forEach((exp) => {
    if (!exp || exp.id == null) return;
    (exp.duties || []).forEach((d) => {
      const description = String(d?.description || '').trim();
      if (!description) return;
      const title =
        String(d?.title || '').trim() ||
        generateDutyTitleFromDescription(description);
      const base = (d && d[BASELINE_KEY]) || {};
      const baselineMatches =
        (base.title ?? '') === title &&
        (base.description ?? '') === description;
      if (d?.id == null) {
        // A row with no id that already matches its baseline was live-created
        // successfully but its id could not be fetched back — skip it so the
        // full-profile refetch (after Save Changes) can reconcile the id,
        // rather than creating a duplicate here.
        const liveCreated = d && d[BASELINE_KEY] && baselineMatches;
        if (!liveCreated) {
          ops.push({
            type: 'create',
            expId: exp.id,
            body: { title, description },
          });
        }
      } else if (!baselineMatches) {
        ops.push({
          type: 'update',
          expId: exp.id,
          dutyId: d.id,
          body: { title, description },
        });
      }
    });
  });
  return ops;
}
